import { FC, useMemo } from 'react';
import type { IceSelection, IceUrlProbeResult } from '@/lib/ice-preflight';
import { parseIceUrl } from '@/lib/ice-preflight-logs';
import { StatusBadge } from '../ui/status-badge';

type RowStatus = 'passed' | 'failed' | 'browser_blocked';

interface UrlRow {
  url: string;
  status: RowStatus;
  probe?: IceUrlProbeResult;
}

const STATUS_LABEL: Record<RowStatus, string> = {
  passed: 'Passed preflight',
  failed: 'Failed preflight',
  browser_blocked: 'Blocked by browser',
};

const STATUS_VARIANT: Record<RowStatus, 'success' | 'warning' | 'alert'> = {
  passed: 'success',
  failed: 'warning',
  browser_blocked: 'alert',
};

function urlsOf(server: RTCIceServer): string {
  return Array.isArray(server.urls) ? server.urls[0] : server.urls;
}

function buildRows(selection: IceSelection): UrlRow[] {
  const survivorUrls = new Set(selection.iceServers.map(urlsOf));
  const resultByUrl = new Map(
    selection.results.map((result) => [result.url, result] as const),
  );
  const rows: UrlRow[] = [];

  // 1. Survivors first — these are what the call will use by default.
  selection.iceServers.forEach((server) => {
    const url = urlsOf(server);
    rows.push({
      url,
      status: 'passed',
      probe: resultByUrl.get(url),
    });
  });

  // 2. Probed URLs that didn't survive — user can force-include them.
  selection.results.forEach((result) => {
    if (survivorUrls.has(result.url)) return;
    rows.push({ url: result.url, status: 'failed', probe: result });
  });

  // 3. Browser-blocked URLs — informational only, never selectable.
  selection.blockedByBrowser.forEach((entry) => {
    rows.push({ url: entry.url, status: 'browser_blocked' });
  });

  return rows;
}

interface IceSettingsPanelProps {
  iceSelection: IceSelection | null;
  excludedIceUrls: Set<string>;
  onToggle: (url: string) => void;
  disabled: boolean;
}

const IceSettingsPanel: FC<IceSettingsPanelProps> = ({
  iceSelection,
  excludedIceUrls,
  onToggle,
  disabled,
}) => {
  const rows = useMemo(
    () => (iceSelection ? buildRows(iceSelection) : []),
    [iceSelection],
  );

  if (!iceSelection) {
    return (
      <div className="p-6 text-sm text-gray-600">
        Run preflight by opening a call to inspect ICE servers.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 overflow-y-auto p-6">
      <p className="text-sm text-gray-600">
        Uncheck a URL to exclude it from this call. Changes apply when you place
        the call.
      </p>
      <ul className="flex flex-col gap-3">
        {rows.map((row) => {
          const info = parseIceUrl(row.url);
          const checked =
            row.status !== 'browser_blocked' && !excludedIceUrls.has(row.url);
          const inputDisabled = disabled || row.status === 'browser_blocked';
          return (
            <li
              className="flex flex-col gap-1.5 rounded-lg border border-gray-200 p-3"
              key={row.url}
            >
              <label className="flex items-start gap-3">
                <input
                  aria-label={`Include ${row.url}`}
                  checked={checked}
                  className="mt-1 h-4 w-4"
                  disabled={inputDisabled}
                  onChange={() => onToggle(row.url)}
                  type="checkbox"
                />
                <div className="flex flex-1 flex-col gap-1">
                  <code className="break-all text-xs font-medium text-gray-900">
                    {row.url}
                  </code>
                  {info && (
                    <div className="text-xs text-gray-500">
                      {info.scheme.toUpperCase()} · {info.host}:{info.port} ·{' '}
                      {info.transport.toUpperCase()}
                    </div>
                  )}
                  {row.probe && (
                    <div className="text-xs text-gray-500">
                      {row.probe.elapsedMs}ms · srflx {row.probe.counters.srflx}{' '}
                      · relay {row.probe.counters.relay}
                      {row.probe.timedOut ? ' · timed out' : ''}
                    </div>
                  )}
                </div>
                <StatusBadge
                  text={STATUS_LABEL[row.status]}
                  variant={STATUS_VARIANT[row.status]}
                />
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default IceSettingsPanel;
